import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  CalendarPicker,
  LoadingButton,
  LocalizationProvider,
  MobileDatePicker,
  MobileTimePicker,
} from '@mui/lab';
import {
  Autocomplete,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  SxProps,
  TextFieldProps,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  DateRange as DateRangeIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import AdapterMoment from '@mui/lab/AdapterMoment';
import moment, { Moment } from 'moment';

import { useAppSelector } from '../../../../hooks';
import {
  classDurationOptions,
  rescheduleReasonOptions,
} from '../../../../constants';
import {
  getSectionByIdAsUser,
  getSectionsState,
  ISectionForUser,
  rescheduleCoursZoomeSectionAsUser,
  resetUserSection,
  TRescheduleCourseZoomSectionAsUser,
} from '../../../../store/V2/SectionSlice';
import {
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../components/Inputs';
import BasicLoader from '../../../../components/Loaders/BasicLoader';
import { getUserAvailabilitySlotOptionsDataAsUser } from '../../../../store/V2/UserAvailabilitySlice/api';
import { getAuthState } from '../../../../store/AuthSlice';
import {
  getUserAvailabilityState,
  resetUserAvailabilitySlotOptionsData,
  TUserAvailabilitySlotOption,
} from '../../../../store/V2/UserAvailabilitySlice';
import {
  IDENTIFIED_USER_ROLES,
  TIdentifiedUserRole,
} from '../../../../store/UserSlice';

interface ClassRescheduleModalProps {
  open: boolean;
  onClose: () => void;
  sectionId: string;
  onSubmitted: (section: ISectionForUser) => void;
  identifiedUserRole: TIdentifiedUserRole;
}

type TSelectOption = {
  label: string;
  value: string;
};

type TState = {
  title: string;
  classDuration: TSelectOption;
  selectedReason: TSelectOption | null;
  otherReason: string;
  selectedDate: Moment;
  selectedSlot: TUserAvailabilitySlotOption | undefined;
  isCustomTimeSelectionEnabled: boolean;
  customDateTime: Moment | null;
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const alertStyles: React.CSSProperties = {
  borderRadius: '4px',
  backgroundColor: '#F1F5F9',
  color: '#1E293B',
  padding: '10px 14px',
  textAlign: 'center',
};

const actionButtonStyles: SxProps = {
  borderRadius: '5px',
  padding: { xs: '8px 7px', sm: '16px 14px' },
  fontWeight: 600,
  fontSize: { xs: '12px', sm: '14px' },
  maxHeight: '48px',
};

const initialState: TState = {
  title: '',
  classDuration: classDurationOptions[3],
  selectedReason: null,
  otherReason: '',
  selectedDate: moment(),
  selectedSlot: undefined,
  isCustomTimeSelectionEnabled: false,
  customDateTime: null,
};

const ClassRescheduleModal: FunctionComponent<ClassRescheduleModalProps> = ({
  open,
  onClose,
  sectionId,
  onSubmitted,
  identifiedUserRole,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isXsScreen = useMediaQuery(theme.breakpoints.only('xs'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  // Store
  const authState = useAppSelector(getAuthState);
  const sectionsState = useAppSelector(getSectionsState);
  const userAvailabilityState = useAppSelector(getUserAvailabilityState);

  // State
  const [state, setState] = useState({ ...initialState });
  const [isLoading, setLoading] = useState(false);

  const authUser = authState.user;
  const sectionData = sectionsState.userSection;
  const availabilitySlotOptionsData =
    userAvailabilityState.userAvailabilitySlotOptionsData;

  const isSlotsLoading =
    userAvailabilityState.status === 'loading' && !availabilitySlotOptionsData;

  const isSubmitDisabled =
    !state.title.trim().length ||
    !state.classDuration ||
    !state.selectedReason ||
    (state.selectedReason &&
      state.selectedReason.value === 'Other' &&
      !state.otherReason.trim().length) ||
    (!state.selectedSlot && !state.isCustomTimeSelectionEnabled) ||
    (!state.customDateTime && state.isCustomTimeSelectionEnabled);

  useEffect(() => {
    dispatch(getSectionByIdAsUser({ id: sectionId }));

    return () => {
      dispatch(resetUserSection());
      dispatch(resetUserAvailabilitySlotOptionsData());
    };
  }, []);

  useEffect(() => {
    if (sectionData) {
      const newStateData: Partial<TState> = {
        title: sectionData.title,
      };

      const matchingDurationOption = classDurationOptions.find(
        (item) => item.value === sectionData.duration?.toString()
      );

      if (matchingDurationOption) {
        newStateData.classDuration = matchingDurationOption;
      }

      updateStateAttributes(newStateData);

      if (authUser) {
        dispatch(
          getUserAvailabilitySlotOptionsDataAsUser({
            sectionId,
            userId: authUser._id,
            date: state.selectedDate.toISOString(),
            duration: newStateData.classDuration
              ? parseFloat(newStateData.classDuration.value)
              : parseFloat(state.classDuration.value),
          })
        );
      }
    }
  }, [sectionData]);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const handleDurationChanged = (
    event: React.SyntheticEvent,
    value: TSelectOption
  ) => {
    if (value) {
      updateStateAttributes({ classDuration: value, selectedSlot: undefined });
      dispatch(resetUserAvailabilitySlotOptionsData());

      if (authUser && !state.isCustomTimeSelectionEnabled) {
        dispatch(
          getUserAvailabilitySlotOptionsDataAsUser({
            sectionId,
            userId: authUser._id,
            date: state.selectedDate.toISOString(),
            duration: parseFloat(value.value),
          })
        );
      }
    }
  };

  const handleDateChange = (date: Moment | null) => {
    if (date) {
      updateStateAttributes({ selectedDate: date, selectedSlot: undefined });
      dispatch(resetUserAvailabilitySlotOptionsData());

      if (authUser && !state.isCustomTimeSelectionEnabled) {
        dispatch(
          getUserAvailabilitySlotOptionsDataAsUser({
            sectionId,
            userId: authUser._id,
            date: date.toISOString(),
            duration: parseFloat(state.classDuration.value),
          })
        );
      }
    }
  };

  const toggleCustomTimeSelection = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    if (checked) {
      dispatch(resetUserAvailabilitySlotOptionsData());
    } else if (authUser) {
      dispatch(
        getUserAvailabilitySlotOptionsDataAsUser({
          sectionId,
          userId: authUser._id,
          date: state.selectedDate.toISOString(),
          duration: parseFloat(state.classDuration.value),
        })
      );
    }

    updateStateAttributes({
      isCustomTimeSelectionEnabled: checked,
      selectedSlot: undefined,
    });
  };

  const handleSubmit = async () => {
    setLoading(true);

    let _selectedSlot: TUserAvailabilitySlotOption | undefined;
    let _isReschduleFreeSlot = false;

    if (state.isCustomTimeSelectionEnabled && state.customDateTime) {
      const _start = moment(
        `${state.selectedDate.format(
          'YYYY-MM-DD'
        )} ${state.customDateTime.format('HH:mm')}`
      );

      _selectedSlot = {
        start: _start.toISOString(),
        end: _start
          .clone()
          .add(parseFloat(state.classDuration.value), 'minutes')
          .toISOString(),
      };
    } else if (!state.isCustomTimeSelectionEnabled && state.selectedSlot) {
      _selectedSlot = state.selectedSlot;
      _isReschduleFreeSlot = true;
    }

    if (state.selectedReason && _selectedSlot) {
      const apiData: TRescheduleCourseZoomSectionAsUser = {
        sectionId,
        title: state.title,
        duration: parseFloat(state.classDuration.value),
        reason: state.selectedReason.value,
        start: _selectedSlot.start,
        end: _selectedSlot.end,
        isReschduleFreeSlot: _isReschduleFreeSlot,
      };

      if (state.selectedReason.value === 'Other') {
        apiData.reason = state.otherReason.trim();
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res: any = await dispatch(
        rescheduleCoursZoomeSectionAsUser(apiData)
      );

      if (res.payload) {
        onSubmitted(res.payload);
      }
    }

    setLoading(false);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px', maxWidth: '1000px' },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: isXsScreen ? '15px' : '30px 30px 20px 30px',
          color: '#000000',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        Reschedule Class
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: isXsScreen ? '7px' : '22px',
          top: isXsScreen ? '7px' : '22px',
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent sx={{ p: isXsScreen ? '0 15px' : '0 30px' }}>
        <div role="presentation">
          {sectionsState.status === 'loading' && !sectionData ? (
            <Stack sx={{ p: '32px' }}>
              <BasicLoader />
            </Stack>
          ) : (
            <Stack>
              <div>
                {identifiedUserRole === IDENTIFIED_USER_ROLES.TUTOR ? (
                  <>
                    <div style={{ ...alertStyles, marginBottom: '20px' }}>
                      <Typography fontWeight={500} fontSize="12px">
                        Please note that, all tutor&apos;s requests for class
                        reschedule require admin approval. If approved, parent
                        and student will be notified.
                      </Typography>
                    </div>
                    <div
                      style={{
                        ...alertStyles,
                        backgroundColor: '#fdebeb',
                        color: '#ff2b2d',
                        marginBottom: '20px',
                      }}
                    >
                      <Typography fontWeight={500} fontSize="12px">
                        In the event your requested reschedule is not accepted
                        by the student, a cancellation penalty will be applied
                        if the class does not take place.
                      </Typography>
                    </div>
                  </>
                ) : null}
                <Grid container columnGap="30px" rowGap="20px">
                  <Grid item xs={12} lg style={{ width: '390px' }}>
                    <Stack>
                      <div style={{ marginBottom: '20px' }}>
                        <Typography
                          color="#475569"
                          fontSize="14px"
                          fontWeight={400}
                          lineHeight="17px"
                        >
                          Please note that, all requests for class rescheduling
                          require admin approval.
                        </Typography>
                      </div>
                      <div style={{ marginBottom: '15px' }}>
                        <FormControl fullWidth>
                          <TPTextField
                            {...textFieldProps}
                            label="Class Title *"
                            placeholder="Enter Here..."
                            value={state.title}
                            onChange={(e) =>
                              updateStateAttributes({ title: e.target.value })
                            }
                          />
                        </FormControl>
                      </div>
                      {!isDesktop ? (
                        <div style={{ marginBottom: '15px' }}>
                          <FormControl fullWidth>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                              <MobileDatePicker
                                disabled={isSlotsLoading}
                                value={state.selectedDate}
                                minDate={moment()}
                                onChange={handleDateChange}
                                label="Date"
                                inputFormat="YYYY MMM DD"
                                renderInput={(params) => (
                                  <TPTextField
                                    {...params}
                                    {...textFieldProps}
                                    size="small"
                                    placeholder="Exam Sitting"
                                    InputProps={{
                                      ...params.InputProps,
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <DateRangeIcon />
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                )}
                              />
                            </LocalizationProvider>
                          </FormControl>
                        </div>
                      ) : null}
                      <div style={{ marginBottom: '15px' }}>
                        <FormControl fullWidth>
                          <Autocomplete
                            size="small"
                            disableClearable
                            disabled={isSlotsLoading}
                            value={state.classDuration}
                            options={classDurationOptions}
                            popupIcon={TPAutoCompleteArrowIconComponent}
                            onChange={handleDurationChanged}
                            isOptionEqualToValue={(option, value) =>
                              option.label === value.value
                            }
                            renderInput={(params) => (
                              <TPTextField
                                {...params}
                                {...textFieldProps}
                                size="small"
                                label="Class Duration *"
                                placeholder="Class Duration"
                              />
                            )}
                          />
                        </FormControl>
                      </div>
                      <div>
                        <Autocomplete
                          fullWidth
                          size="small"
                          value={state.selectedReason}
                          disabled={isSlotsLoading}
                          options={[
                            ...rescheduleReasonOptions,
                            { label: 'Other', value: 'Other' },
                          ]}
                          onChange={(e, value) =>
                            updateStateAttributes({ selectedReason: value })
                          }
                          popupIcon={TPAutoCompleteArrowIconComponent}
                          isOptionEqualToValue={(option, value) =>
                            option.label === value.value
                          }
                          renderInput={(params) => (
                            <TPTextField
                              {...params}
                              {...textFieldProps}
                              size="small"
                              label="Reason for rescheduling *"
                              placeholder="Select"
                            />
                          )}
                        />
                      </div>
                      <Collapse
                        in={Boolean(
                          state.selectedReason &&
                            state.selectedReason.value === 'Other'
                        )}
                      >
                        <div style={{ marginTop: '15px' }}>
                          <TPTextField
                            {...textFieldProps}
                            fullWidth
                            size="small"
                            label="Other Reason *"
                            disabled={isSlotsLoading}
                            placeholder="Enter here..."
                            value={state.otherReason}
                            onChange={(e) =>
                              updateStateAttributes({
                                otherReason: e.target.value,
                              })
                            }
                          />
                        </div>
                      </Collapse>
                    </Stack>
                  </Grid>
                  {isDesktop ? (
                    <Grid item xs={12} lg style={{ width: '300px' }}>
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <Box
                          sx={{
                            '& .css-dhopo2': {
                              minHeight: '200px',
                            },
                          }}
                        >
                          <CalendarPicker
                            disabled={isSlotsLoading}
                            views={['day']}
                            minDate={moment()}
                            date={state.selectedDate}
                            onChange={handleDateChange}
                          />
                        </Box>
                      </LocalizationProvider>
                    </Grid>
                  ) : null}
                  <Grid
                    item
                    xs={12}
                    lg
                    sx={{ maxHeight: '300px', overflowY: 'auto' }}
                  >
                    <Stack direction="row" justifyContent="flex-end">
                      <FormControlLabel
                        label="Request Time"
                        control={
                          <Switch
                            checked={state.isCustomTimeSelectionEnabled}
                            onChange={toggleCustomTimeSelection}
                          />
                        }
                      />
                    </Stack>
                    <Collapse in={state.isCustomTimeSelectionEnabled}>
                      <div>
                        <FormControl fullWidth>
                          <LocalizationProvider dateAdapter={AdapterMoment}>
                            <MobileTimePicker
                              value={state.customDateTime}
                              label="Custom Time"
                              onChange={(date) => {
                                if (date) {
                                  updateStateAttributes({
                                    customDateTime: date,
                                  });
                                }
                              }}
                              renderInput={(params) => (
                                <TPTextField
                                  {...params}
                                  {...textFieldProps}
                                  size="small"
                                  placeholder="Select Time"
                                  InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <AccessTimeIcon />
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              )}
                            />
                          </LocalizationProvider>
                        </FormControl>
                      </div>
                    </Collapse>
                    <Collapse in={!state.isCustomTimeSelectionEnabled}>
                      <div>
                        <div>
                          <Typography fontWeight={600} fontSize="16px">
                            Available Time Slots
                          </Typography>
                        </div>
                        {availabilitySlotOptionsData &&
                        availabilitySlotOptionsData.list &&
                        availabilitySlotOptionsData.list.length ? (
                          <Stack rowGap=".5rem" mt="15px">
                            {availabilitySlotOptionsData.list.map(
                              (slotItem, index) => {
                                const isSelected =
                                  state.selectedSlot &&
                                  JSON.stringify(state.selectedSlot) ===
                                    JSON.stringify(slotItem);

                                return (
                                  <Card
                                    key={`slot-${index}`}
                                    elevation={0}
                                    sx={{
                                      border: '1px solid #01A79F',
                                      borderRadius: '.375rem',
                                      backgroundColor: isSelected
                                        ? '#01A79F'
                                        : undefined,
                                    }}
                                  >
                                    <CardActionArea
                                      onClick={() =>
                                        updateStateAttributes({
                                          selectedSlot: slotItem,
                                        })
                                      }
                                    >
                                      <CardContent sx={{ p: '.5rem 1rem' }}>
                                        <Typography
                                          fontSize="14px"
                                          fontWeight={500}
                                          color={
                                            isSelected ? '#ffffff' : '#01A79F'
                                          }
                                          textAlign="center"
                                        >
                                          {moment(slotItem.start).format(
                                            'hh:mm A'
                                          )}
                                        </Typography>
                                      </CardContent>
                                    </CardActionArea>
                                  </Card>
                                );
                              }
                            )}
                          </Stack>
                        ) : null}
                        {isSlotsLoading ? (
                          <Stack sx={{ p: '15px' }}>
                            <BasicLoader loaderProps={{ size: 30 }} />
                          </Stack>
                        ) : null}
                        {userAvailabilityState.status !== 'loading' &&
                        availabilitySlotOptionsData &&
                        !(availabilitySlotOptionsData.list || []).length ? (
                          <Stack>
                            <Typography fontSize="14px" fontWeight={500}>
                              No slots are available
                            </Typography>
                          </Stack>
                        ) : null}
                      </div>
                    </Collapse>
                  </Grid>
                </Grid>
              </div>
            </Stack>
          )}
        </div>
      </DialogContent>
      {sectionData ? (
        <DialogActions sx={{ p: isXsScreen ? '15px' : '30px' }}>
          <LoadingButton
            disableElevation
            variant="contained"
            loading={isLoading}
            disabled={isSubmitDisabled}
            onClick={handleSubmit}
            sx={{
              ...actionButtonStyles,
              color: '#ffffff',
              backgroundColor: '#1E293B',
              '&:hover': {
                backgroundColor: '#1E293B',
              },
            }}
          >
            Reschedule
          </LoadingButton>
        </DialogActions>
      ) : null}
    </Dialog>
  );
};

export default ClassRescheduleModal;
